import React, { FC, useState } from 'react';
import { Flex, FlexItem, Heading, IconInteractive, IconClose } from 'snacks-design-system';
import styles from './CourierDetailActionsConfirm.module.scss';
import { useTranslation } from 'react-i18next';
import { EBottomSheetAction, TBottomSheetAction } from '../CourierBottomSheet';
import { useQueryClient } from 'react-query';
import { NotificationCategories, useNotificationsActions } from '@src/shared/context/notifications';
import { useUpdateTips } from '../../queries';
import {
  trackManageCourierTipsToggleOff,
  trackManageCourierTipsToggleOn,
} from '../../tracking/courier-card';
import { useKeycloakUserState } from '@src/shared/context/keycloak-user';
import PIEText from '@src/shared/components/PIEText';
import PIEButton from '@src/shared/components/PIEButton';

interface ICourierDetailActionsConfirmProps {
  onAction: (action: TBottomSheetAction) => void;
  onDismiss: () => void;
  bottomSheetAction: TBottomSheetAction;
}

export const CourierDetailActionsConfirm: FC<ICourierDetailActionsConfirmProps> = ({
  onAction,
  onDismiss,
  bottomSheetAction,
}) => {
  const { t } = useTranslation();
  const handleOnConfirm = () => {
    setIsPromising(true);
    updateTips.mutate(bottomSheetAction.source.data.isSwitchOn);
    onAction({ type: EBottomSheetAction.Confirm, source: {} });
  };
  const queryClient = useQueryClient();
  const { notifyInformation, notifyError } = useNotificationsActions(
    NotificationCategories.CourierManagement
  );
  const [isPromising, setIsPromising] = useState<boolean>();
  const { restaurant } = useKeycloakUserState();

  const updateTips = useUpdateTips({
    onSuccess: () => {
      notifyInformation(
        `${
          bottomSheetAction.source.data.isSwitchOn
            ? t('tips.NOTIFICATION_TIPS_SHOWN_SUCCESS')
            : t('tips.NOTIFICATION_TIPS_HIDE_SUCCESS')
        }`
      );

      if (bottomSheetAction.source.data.isSwitchOn === true) {
        trackManageCourierTipsToggleOn(restaurant?.country as any, restaurant?.language as any);
      }
      if (bottomSheetAction.source.data.isSwitchOn === false) {
        trackManageCourierTipsToggleOff(restaurant?.country as any, restaurant?.language as any);
      }
      queryClient.invalidateQueries('couriers');
      queryClient.invalidateQueries('settings');
    },
    onError: () => {
      setIsPromising(false);
    },
    onSettled: () => {
      setIsPromising(false);
      onDismiss();
    },
  });

  return (
    <div className={styles.confirmation_container}>
      <div>
        <Flex>
          <FlexItem grow={1}>
            <Heading level={4}>{t('tips.CONFIRMATION_MODAL_TITLE')}</Heading>
          </FlexItem>
          <FlexItem shrink={1}>
            <IconInteractive onClick={onDismiss}>
              <IconClose size="m" />
            </IconInteractive>
          </FlexItem>
        </Flex>
      </div>
      <div>
        <PIEText size="var(--xds-font-size-16)" text={t('tips.CONFIRMATION_MODAL_DESCRIPTION')} />
      </div>
      <div className={styles.confirmation_footer}>
        <PIEButton
          onClick={onDismiss}
          size="medium"
          mode="ghost"
          text={t('tips.IGNORE_CHANGES')}
          type={'button'}
        />

        <PIEButton
          className={styles.confirmation_button}
          isPromising={isPromising}
          onClick={handleOnConfirm}
          size="medium"
          mode="primary"
          text={t('tips.CONFIRM_CHANGES')}
          type={'button'}
        />
      </div>
    </div>
  );
};

export default CourierDetailActionsConfirm;
